import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Sword, Shield, Wand, Heart, Zap, Star, Coins, Book, Backpack, Settings, Save, Scroll, Award, ArrowRight } from 'lucide-react';
import { Howl } from 'howler';
import { ASSETS } from './assetManifest';

// Game constants
const TICK_RATE = 1000; // 1 second per tick
const SAVE_INTERVAL = 60000; // Save every minute

// Hero classes
const HERO_CLASSES = {
  WARRIOR: {
    name: 'Warrior',
    icon: <Sword size={24} />,
    baseStats: { strength: 10, agility: 5, intelligence: 3, vitality: 8 },
    description: 'A mighty warrior skilled in close combat and defense.'
  },
  MAGE: {
    name: 'Mage',
    icon: <Wand size={24} />,
    baseStats: { strength: 3, agility: 5, intelligence: 10, vitality: 4 },
    description: 'A powerful spellcaster with mastery over arcane magic.'
  },
  RANGER: {
    name: 'Ranger',
    icon: <Zap size={24} />,
    baseStats: { strength: 6, agility: 10, intelligence: 6, vitality: 5 },
    description: 'A skilled hunter with exceptional speed and precision.'
  }
};

// Adventure zones
const ADVENTURE_ZONES = [
  {
    id: 'forest',
    name: 'Enchanted Forest',
    level: 1,
    monsters: ['Wolf', 'Goblin', 'Bandit'],
    rewards: { gold: 5, experience: 10 },
    requiredLevel: 1
  },
  {
    id: 'caves',
    name: 'Dark Caves',
    level: 5,
    monsters: ['Skeleton', 'Bat Swarm', 'Cave Troll'],
    rewards: { gold: 15, experience: 30 },
    requiredLevel: 5
  },
  {
    id: 'mountains',
    name: 'Misty Mountains',
    level: 10,
    monsters: ['Harpy', 'Mountain Ogre', 'Stone Golem'],
    rewards: { gold: 30, experience: 60 },
    requiredLevel: 10
  },
  {
    id: 'castle',
    name: 'Abandoned Castle',
    level: 15,
    monsters: ['Ghost Knight', 'Vampire', 'Lich'],
    rewards: { gold: 50, experience: 100 },
    requiredLevel: 15
  }
];

// Upgrades
const UPGRADES = [
  {
    id: 'weapon',
    name: 'Weapon Upgrade',
    description: 'Increase your attack power',
    baseEffect: 2,
    baseCost: 50,
    costMultiplier: 1.5,
    stat: 'attack'
  },
  {
    id: 'armor',
    name: 'Armor Upgrade',
    description: 'Increase your defense',
    baseEffect: 2,
    baseCost: 50,
    costMultiplier: 1.5,
    stat: 'defense'
  },
  {
    id: 'training',
    name: 'Combat Training',
    description: 'Gain experience faster',
    baseEffect: 0.1, // 10% increase
    baseCost: 100,
    costMultiplier: 2,
    stat: 'experienceMultiplier'
  },
  {
    id: 'goldFind',
    name: 'Treasure Hunter',
    description: 'Find more gold during adventures',
    baseEffect: 0.1, // 10% increase
    baseCost: 100,
    costMultiplier: 2,
    stat: 'goldMultiplier'
  }
];

// Sound effects
const SOUNDS = {
  click: new Howl({ src: [ASSETS.sounds.click.path] }),
  levelUp: new Howl({ src: [ASSETS.sounds.levelUp.path] }),
  battle: new Howl({ src: [ASSETS.sounds.battle.path] }),
  purchase: new Howl({ src: [ASSETS.sounds.purchase.path] }),
  victory: new Howl({ src: [ASSETS.sounds.victory.path] })
};

function App() {
  // Game state
  const [gameStarted, setGameStarted] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [hero, setHero] = useState(null);
  const [resources, setResources] = useState({
    gold: 0,
    experience: 0
  });
  const [upgrades, setUpgrades] = useState({});
  const [activeZone, setActiveZone] = useState(null);
  const [adventureLog, setAdventureLog] = useState([]);
  const [activeTab, setActiveTab] = useState('character');
  const [lastSaved, setLastSaved] = useState(null);
  const [notification, setNotification] = useState(null);

  // Initialize upgrades
  useEffect(() => {
    const initialUpgrades = {};
    UPGRADES.forEach(upgrade => {
      initialUpgrades[upgrade.id] = {
        level: 0,
        cost: upgrade.baseCost,
        effect: upgrade.baseEffect
      };
    });
    setUpgrades(initialUpgrades);
  }, []);

  // Load game from localStorage
  useEffect(() => {
    const savedGame = localStorage.getItem('fantasyIdleGame');
    if (savedGame) {
      try {
        const parsedGame = JSON.parse(savedGame);
        setGameStarted(parsedGame.gameStarted);
        setSelectedClass(parsedGame.selectedClass);
        setHero(parsedGame.hero);
        setResources(parsedGame.resources);
        setUpgrades(parsedGame.upgrades);
        setActiveZone(parsedGame.activeZone);
        setAdventureLog(parsedGame.adventureLog);
        setLastSaved(new Date());
        
        showNotification('Game loaded successfully!');
      } catch (error) {
        console.error('Error loading game:', error);
      }
    }
  }, []);

  // Save game to localStorage
  const saveGame = useCallback(() => {
    const gameState = {
      gameStarted,
      selectedClass,
      hero,
      resources,
      upgrades,
      activeZone,
      adventureLog
    };
    
    localStorage.setItem('fantasyIdleGame', JSON.stringify(gameState));
    setLastSaved(new Date());
    showNotification('Game saved!');
    SOUNDS.click.play();
  }, [gameStarted, selectedClass, hero, resources, upgrades, activeZone, adventureLog]);

  // Auto-save game
  useEffect(() => {
    if (gameStarted) {
      const saveInterval = setInterval(saveGame, SAVE_INTERVAL);
      return () => clearInterval(saveInterval);
    }
  }, [gameStarted, saveGame]);

  // Game tick for idle progression
  useEffect(() => {
    if (gameStarted && hero) {
      const gameInterval = setInterval(() => {
        // Passive gold generation
        const goldPerTick = Math.floor(hero.level * 0.5 * (1 + (upgrades.goldFind?.level || 0) * upgrades.goldFind?.effect));
        
        // If on an adventure, generate more resources
        if (activeZone) {
          const zone = ADVENTURE_ZONES.find(z => z.id === activeZone);
          if (zone) {
            const goldMultiplier = 1 + (upgrades.goldFind?.level || 0) * upgrades.goldFind?.effect;
            const expMultiplier = 1 + (upgrades.training?.level || 0) * upgrades.training?.effect;
            
            const goldGain = Math.floor(zone.rewards.gold * goldMultiplier);
            const expGain = Math.floor(zone.rewards.experience * expMultiplier);
            
            setResources(prev => ({
              gold: prev.gold + goldGain,
              experience: prev.experience + expGain
            }));
            
            // Random chance to generate adventure log
            if (Math.random() < 0.3) {
              const monster = zone.monsters[Math.floor(Math.random() * zone.monsters.length)];
              addToAdventureLog(`${hero.name} defeated a ${monster} in ${zone.name}!`);
              SOUNDS.battle.play();
            }
          }
        } else {
          // Passive gold when not adventuring
          setResources(prev => ({
            ...prev,
            gold: prev.gold + goldPerTick
          }));
        }
        
        // Check for level up
        checkLevelUp();
      }, TICK_RATE);
      
      return () => clearInterval(gameInterval);
    }
  }, [gameStarted, hero, activeZone, upgrades]);

  // Check if hero can level up
  const checkLevelUp = useCallback(() => {
    if (hero) {
      const expRequired = Math.floor(100 * Math.pow(1.5, hero.level - 1));
      
      if (resources.experience >= expRequired) {
        setHero(prev => ({
          ...prev,
          level: prev.level + 1,
          attack: Math.floor(prev.attack * 1.2),
          defense: Math.floor(prev.defense * 1.2)
        }));
        
        setResources(prev => ({
          ...prev,
          experience: prev.experience - expRequired
        }));
        
        addToAdventureLog(`${hero.name} reached level ${hero.level + 1}!`);
        SOUNDS.levelUp.play();
        showNotification(`Level Up! ${hero.name} is now level ${hero.level + 1}`);
      }
    }
  }, [hero, resources.experience]);

  // Add message to adventure log
  const addToAdventureLog = useCallback((message) => {
    setAdventureLog(prev => {
      const newLog = [{
        message,
        timestamp: new Date().toLocaleTimeString()
      }, ...prev];
      
      // Keep only the last 20 messages
      return newLog.slice(0, 20);
    });
  }, []);

  // Show notification
  const showNotification = useCallback((message) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Create a new hero
  const createHero = useCallback((name, heroClass) => {
    const classData = HERO_CLASSES[heroClass];
    const newHero = {
      name,
      class: heroClass,
      level: 1,
      attack: classData.baseStats.strength * 2,
      defense: classData.baseStats.vitality * 2,
      stats: { ...classData.baseStats }
    };
    
    setHero(newHero);
    setGameStarted(true);
    addToAdventureLog(`${name} the ${classData.name} begins their adventure!`);
    SOUNDS.click.play();
  }, [addToAdventureLog]);

  // Start an adventure
  const startAdventure = useCallback((zoneId) => {
    const zone = ADVENTURE_ZONES.find(z => z.id === zoneId);
    
    if (zone && hero.level >= zone.requiredLevel) {
      setActiveZone(zoneId);
      addToAdventureLog(`${hero.name} started exploring ${zone.name}.`);
      SOUNDS.click.play();
    } else {
      showNotification(`You need to be level ${zone.requiredLevel} to explore this area.`);
    }
  }, [hero, addToAdventureLog, showNotification]);

  // End current adventure
  const endAdventure = useCallback(() => {
    if (activeZone) {
      const zone = ADVENTURE_ZONES.find(z => z.id === activeZone);
      addToAdventureLog(`${hero.name} returned from ${zone.name}.`);
      setActiveZone(null);
      SOUNDS.click.play();
    }
  }, [activeZone, hero, addToAdventureLog]);

  // Purchase an upgrade
  const purchaseUpgrade = useCallback((upgradeId) => {
    const upgrade = UPGRADES.find(u => u.id === upgradeId);
    const currentUpgrade = upgrades[upgradeId];
    
    if (resources.gold >= currentUpgrade.cost) {
      setResources(prev => ({
        ...prev,
        gold: prev.gold - currentUpgrade.cost
      }));
      
      setUpgrades(prev => ({
        ...prev,
        [upgradeId]: {
          level: prev[upgradeId].level + 1,
          cost: Math.floor(prev[upgradeId].cost * upgrade.costMultiplier),
          effect: upgrade.baseEffect
        }
      }));
      
      // Update hero stats if needed
      if (upgrade.stat === 'attack' || upgrade.stat === 'defense') {
        setHero(prev => ({
          ...prev,
          [upgrade.stat]: prev[upgrade.stat] + upgrade.baseEffect
        }));
      }
      
      addToAdventureLog(`Purchased ${upgrade.name} (Level ${currentUpgrade.level + 1})`);
      SOUNDS.purchase.play();
      showNotification(`Upgraded ${upgrade.name} to level ${currentUpgrade.level + 1}`);
    } else {
      showNotification("Not enough gold!");
    }
  }, [resources.gold, upgrades, addToAdventureLog, showNotification]);

  // Character creation screen
  if (!gameStarted || !hero) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-900 to-blue-900 flex flex-col items-center justify-center p-4 text-white">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-bold mb-2 text-yellow-300">Fantasy Idle</h1>
          <p className="text-xl text-blue-200">Begin your epic adventure in a world of magic and monsters</p>
        </motion.div>
        
        {!selectedClass ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-blue-800 bg-opacity-50 p-6 rounded-lg shadow-lg max-w-2xl w-full"
          >
            <h2 className="text-2xl font-bold mb-4 text-center">Choose Your Hero Class</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(HERO_CLASSES).map(([classKey, classData]) => (
                <motion.div
                  key={classKey}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-blue-700 bg-opacity-60 p-4 rounded-lg cursor-pointer hover:bg-blue-600 transition-colors"
                  onClick={() => {
                    setSelectedClass(classKey);
                    SOUNDS.click.play();
                  }}
                >
                  <div className="flex justify-center mb-2 text-yellow-300">
                    {classData.icon}
                  </div>
                  <h3 className="text-xl font-bold text-center mb-2">{classData.name}</h3>
                  <p className="text-sm text-blue-200 mb-3">{classData.description}</p>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className="flex items-center"><Sword size={12} className="mr-1" /> STR: {classData.baseStats.strength}</div>
                    <div className="flex items-center"><Zap size={12} className="mr-1" /> AGI: {classData.baseStats.agility}</div>
                    <div className="flex items-center"><Book size={12} className="mr-1" /> INT: {classData.baseStats.intelligence}</div>
                    <div className="flex items-center"><Heart size={12} className="mr-1" /> VIT: {classData.baseStats.vitality}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-blue-800 bg-opacity-50 p-6 rounded-lg shadow-lg max-w-md w-full"
          >
            <h2 className="text-2xl font-bold mb-4 text-center">Name Your Hero</h2>
            <div className="flex items-center justify-center mb-4">
              <div className="text-yellow-300 mr-3">
                {HERO_CLASSES[selectedClass].icon}
              </div>
              <span className="text-xl">{HERO_CLASSES[selectedClass].name}</span>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const name = e.target.heroName.value.trim();
              if (name) {
                createHero(name, selectedClass);
              }
            }}>
              <input
                type="text"
                name="heroName"
                placeholder="Enter hero name..."
                className="w-full p-2 rounded bg-blue-900 text-white border border-blue-500 mb-4 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                maxLength={20}
                required
              />
              
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setSelectedClass(null)}
                  className="px-4 py-2 bg-blue-700 rounded hover:bg-blue-600 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-yellow-600 rounded hover:bg-yellow-500 transition-colors"
                >
                  Begin Adventure
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </div>
    );
  }

  // Main game UI
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-blue-900 text-white">
      {/* Header */}
      <header className="bg-blue-900 bg-opacity-80 p-4 shadow-md">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center">
          <h1 className="text-3xl font-bold text-yellow-300 mb-2 md:mb-0">Fantasy Idle</h1>
          
          <div className="flex space-x-6">
            <div className="flex items-center">
              <Coins className="text-yellow-400 mr-2" size={20} />
              <span className="font-medium">{Math.floor(resources.gold)}</span>
            </div>
            
            <div className="flex items-center">
              <Star className="text-blue-300 mr-2" size={20} />
              <span className="font-medium">{Math.floor(resources.experience)}</span>
            </div>
            
            <div className="flex items-center">
              <Award className="text-green-400 mr-2" size={20} />
              <span className="font-medium">Level {hero.level}</span>
            </div>
          </div>
        </div>
      </header>
      
      {/* Notification */}
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 right-4 bg-blue-700 px-4 py-2 rounded shadow-lg z-50"
        >
          {notification}
        </motion.div>
      )}
      
      {/* Main content */}
      <main className="container mx-auto p-4 grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left sidebar - Character info */}
        <div className="lg:col-span-1">
          <div className="bg-blue-800 bg-opacity-50 rounded-lg shadow-lg p-4 mb-4">
            <div className="flex items-center mb-4">
              <div className="bg-blue-700 p-2 rounded-full mr-3 text-yellow-300">
                {HERO_CLASSES[hero.class].icon}
              </div>
              <div>
                <h2 className="text-xl font-bold">{hero.name}</h2>
                <p className="text-blue-300 text-sm">{HERO_CLASSES[hero.class].name} • Level {hero.level}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-blue-900 bg-opacity-50 p-2 rounded">
                <div className="flex items-center text-sm text-blue-300">
                  <Sword size={14} className="mr-1" /> Attack
                </div>
                <div className="font-medium">{hero.attack}</div>
              </div>
              
              <div className="bg-blue-900 bg-opacity-50 p-2 rounded">
                <div className="flex items-center text-sm text-blue-300">
                  <Shield size={14} className="mr-1" /> Defense
                </div>
                <div className="font-medium">{hero.defense}</div>
              </div>
              
              <div className="bg-blue-900 bg-opacity-50 p-2 rounded">
                <div className="flex items-center text-sm text-blue-300">
                  <Zap size={14} className="mr-1" /> Agility
                </div>
                <div className="font-medium">{hero.stats.agility}</div>
              </div>
              
              <div className="bg-blue-900 bg-opacity-50 p-2 rounded">
                <div className="flex items-center text-sm text-blue-300">
                  <Book size={14} className="mr-1" /> Intelligence
                </div>
                <div className="font-medium">{hero.stats.intelligence}</div>
              </div>
            </div>
            
            {/* Level progress */}
            <div className="mb-2">
              <div className="flex justify-between text-xs mb-1">
                <span>Experience</span>
                <span>{resources.experience} / {Math.floor(100 * Math.pow(1.5, hero.level - 1))}</span>
              </div>
              <div className="w-full bg-blue-900 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ width: `${Math.min(100, (resources.experience / Math.floor(100 * Math.pow(1.5, hero.level - 1))) * 100)}%` }}
                ></div>
              </div>
            </div>
            
            {/* Save button */}
            <button 
              onClick={saveGame}
              className="w-full mt-4 bg-green-700 hover:bg-green-600 py-2 rounded flex items-center justify-center transition-colors"
            >
              <Save size={16} className="mr-2" /> Save Game
            </button>
            
            {lastSaved && (
              <p className="text-xs text-center mt-2 text-blue-300">
                Last saved: {lastSaved.toLocaleTimeString()}
              </p>
            )}
          </div>
          
          {/* Adventure log */}
          <div className="bg-blue-800 bg-opacity-50 rounded-lg shadow-lg p-4">
            <h3 className="text-lg font-bold mb-2 flex items-center">
              <Scroll size={18} className="mr-2" /> Adventure Log
            </h3>
            <div className="max-h-60 overflow-y-auto">
              {adventureLog.length === 0 ? (
                <p className="text-blue-300 text-sm italic">Your adventure awaits...</p>
              ) : (
                <ul className="space-y-2">
                  {adventureLog.map((entry, index) => (
                    <li key={index} className="text-sm border-b border-blue-700 pb-1">
                      <span className="text-blue-300 text-xs">{entry.timestamp}</span>
                      <p>{entry.message}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        
        {/* Main content area */}
        <div className="lg:col-span-3">
          {/* Tabs */}
          <div className="bg-blue-800 bg-opacity-50 rounded-lg shadow-lg mb-4">
            <div className="flex overflow-x-auto">
              <button 
                onClick={() => { setActiveTab('character'); SOUNDS.click.play(); }}
                className={`px-4 py-3 font-medium flex items-center ${activeTab === 'character' ? 'bg-blue-700 text-white' : 'text-blue-300 hover:bg-blue-700 hover:bg-opacity-50'}`}
              >
                <Heart size={18} className="mr-2" /> Character
              </button>
              <button 
                onClick={() => { setActiveTab('adventure'); SOUNDS.click.play(); }}
                className={`px-4 py-3 font-medium flex items-center ${activeTab === 'adventure' ? 'bg-blue-700 text-white' : 'text-blue-300 hover:bg-blue-700 hover:bg-opacity-50'}`}
              >
                <Sword size={18} className="mr-2" /> Adventure
              </button>
              <button 
                onClick={() => { setActiveTab('upgrades'); SOUNDS.click.play(); }}
                className={`px-4 py-3 font-medium flex items-center ${activeTab === 'upgrades' ? 'bg-blue-700 text-white' : 'text-blue-300 hover:bg-blue-700 hover:bg-opacity-50'}`}
              >
                <Star size={18} className="mr-2" /> Upgrades
              </button>
              <button 
                onClick={() => { setActiveTab('inventory'); SOUNDS.click.play(); }}
                className={`px-4 py-3 font-medium flex items-center ${activeTab === 'inventory' ? 'bg-blue-700 text-white' : 'text-blue-300 hover:bg-blue-700 hover:bg-opacity-50'}`}
              >
                <Backpack size={18} className="mr-2" /> Inventory
              </button>
              <button 
                onClick={() => { setActiveTab('settings'); SOUNDS.click.play(); }}
                className={`px-4 py-3 font-medium flex items-center ${activeTab === 'settings' ? 'bg-blue-700 text-white' : 'text-blue-300 hover:bg-blue-700 hover:bg-opacity-50'}`}
              >
                <Settings size={18} className="mr-2" /> Settings
              </button>
            </div>
          </div>
          
          {/* Tab content */}
          <div className="bg-blue-800 bg-opacity-50 rounded-lg shadow-lg p-4">
            {/* Character Tab */}
            {activeTab === 'character' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Character Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg">
                    <h3 className="text-lg font-bold mb-2">Stats</h3>
                    <ul className="space-y-2">
                      <li className="flex justify-between">
                        <span className="flex items-center"><Sword size={16} className="mr-2 text-red-400" /> Strength</span>
                        <span className="font-medium">{hero.stats.strength}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="flex items-center"><Zap size={16} className="mr-2 text-yellow-400" /> Agility</span>
                        <span className="font-medium">{hero.stats.agility}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="flex items-center"><Book size={16} className="mr-2 text-blue-400" /> Intelligence</span>
                        <span className="font-medium">{hero.stats.intelligence}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="flex items-center"><Heart size={16} className="mr-2 text-green-400" /> Vitality</span>
                        <span className="font-medium">{hero.stats.vitality}</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg">
                    <h3 className="text-lg font-bold mb-2">Combat</h3>
                    <ul className="space-y-2">
                      <li className="flex justify-between">
                        <span className="flex items-center"><Sword size={16} className="mr-2 text-red-400" /> Attack</span>
                        <span className="font-medium">{hero.attack}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="flex items-center"><Shield size={16} className="mr-2 text-blue-400" /> Defense</span>
                        <span className="font-medium">{hero.defense}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="flex items-center"><Coins size={16} className="mr-2 text-yellow-400" /> Gold Find</span>
                        <span className="font-medium">+{(upgrades.goldFind?.level || 0) * 10}%</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="flex items-center"><Star size={16} className="mr-2 text-purple-400" /> Experience Gain</span>
                        <span className="font-medium">+{(upgrades.training?.level || 0) * 10}%</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg">
                  <h3 className="text-lg font-bold mb-2">Level Progress</h3>
                  <div className="mb-2">
                    <div className="flex justify-between mb-1">
                      <span>Level {hero.level}</span>
                      <span>{resources.experience} / {Math.floor(100 * Math.pow(1.5, hero.level - 1))} XP</span>
                    </div>
                    <div className="w-full bg-blue-950 rounded-full h-4">
                      <div 
                        className="bg-purple-600 h-4 rounded-full transition-all duration-500" 
                        style={{ width: `${Math.min(100, (resources.experience / Math.floor(100 * Math.pow(1.5, hero.level - 1))) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <p className="text-sm text-blue-300">
                    Next level: +2 to all base stats, increased combat effectiveness
                  </p>
                </div>
              </div>
            )}
            
            {/* Adventure Tab */}
            {activeTab === 'adventure' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold">Adventure Zones</h2>
                  
                  {activeZone && (
                    <button 
                      onClick={endAdventure}
                      className="bg-red-700 hover:bg-red-600 px-3 py-1 rounded text-sm flex items-center"
                    >
                      End Adventure
                    </button>
                  )}
                </div>
                
                {activeZone ? (
                  <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg mb-4">
                    <h3 className="text-xl font-bold mb-2">
                      {ADVENTURE_ZONES.find(z => z.id === activeZone)?.name}
                    </h3>
                    <p className="mb-4 text-blue-200">
                      Your hero is currently exploring this area. Resources are being gathered automatically.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-blue-950 bg-opacity-50 p-3 rounded">
                        <h4 className="text-sm text-blue-300 mb-1">Gold per minute</h4>
                        <p className="font-bold text-yellow-400">
                          +{Math.floor(ADVENTURE_ZONES.find(z => z.id === activeZone)?.rewards.gold * 60 * (1 + (upgrades.goldFind?.level || 0) * upgrades.goldFind?.effect))}
                        </p>
                      </div>
                      
                      <div className="bg-blue-950 bg-opacity-50 p-3 rounded">
                        <h4 className="text-sm text-blue-300 mb-1">XP per minute</h4>
                        <p className="font-bold text-purple-400">
                          +{Math.floor(ADVENTURE_ZONES.find(z => z.id === activeZone)?.rewards.experience * 60 * (1 + (upgrades.training?.level || 0) * upgrades.training?.effect))}
                        </p>
                      </div>
                    </div>
                    
                    <h4 className="font-medium mb-2">Monsters in this area:</h4>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {ADVENTURE_ZONES.find(z => z.id === activeZone)?.monsters.map((monster, idx) => (
                        <span key={idx} className="bg-blue-950 px-2 py-1 rounded text-sm">
                          {monster}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-blue-300 mb-4">
                    Select a zone to begin your adventure. Your hero will automatically gather resources and fight monsters.
                  </p>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ADVENTURE_ZONES.map((zone) => (
                    <motion.div
                      key={zone.id}
                      whileHover={{ scale: 1.02 }}
                      className={`bg-blue-900 bg-opacity-50 p-4 rounded-lg border-2 ${
                        activeZone === zone.id 
                          ? 'border-yellow-500' 
                          : hero.level >= zone.requiredLevel 
                            ? 'border-transparent hover:border-blue-500 cursor-pointer' 
                            : 'border-transparent opacity-70'
                      }`}
                      onClick={() => {
                        if (hero.level >= zone.requiredLevel && activeZone !== zone.id) {
                          startAdventure(zone.id);
                        }
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold">{zone.name}</h3>
                        <span className="bg-blue-950 px-2 py-1 rounded text-xs">
                          Level {zone.requiredLevel}+
                        </span>
                      </div>
                      
                      <p className="text-sm text-blue-300 mb-3">
                        {activeZone === zone.id ? 'Currently exploring...' : 'A dangerous area filled with monsters and treasures.'}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="flex items-center text-sm">
                          <Coins size={14} className="text-yellow-400 mr-1" />
                          <span>{zone.rewards.gold} gold/tick</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Star size={14} className="text-purple-400 mr-1" />
                          <span>{zone.rewards.experience} XP/tick</span>
                        </div>
                      </div>
                      
                      {hero.level < zone.requiredLevel ? (
                        <div className="bg-red-900 bg-opacity-50 p-2 rounded text-center text-sm">
                          Requires Level {zone.requiredLevel}
                        </div>
                      ) : activeZone === zone.id ? (
                        <div className="bg-green-800 bg-opacity-50 p-2 rounded text-center text-sm">
                          Currently Exploring
                        </div>
                      ) : (
                        <div className="bg-blue-800 bg-opacity-50 p-2 rounded text-center text-sm flex items-center justify-center">
                          Start Adventure <ArrowRight size={14} className="ml-1" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Upgrades Tab */}
            {activeTab === 'upgrades' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Upgrades</h2>
                <p className="text-blue-300 mb-4">
                  Enhance your hero's abilities with powerful upgrades.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {UPGRADES.map((upgrade) => {
                    const currentUpgrade = upgrades[upgrade.id];
                    const canAfford = resources.gold >= currentUpgrade.cost;
                    
                    return (
                      <motion.div
                        key={upgrade.id}
                        whileHover={{ scale: 1.02 }}
                        className={`bg-blue-900 bg-opacity-50 p-4 rounded-lg border ${
                          canAfford ? 'border-blue-500 hover:border-green-500 cursor-pointer' : 'border-transparent opacity-80'
                        }`}
                        onClick={() => {
                          if (canAfford) {
                            purchaseUpgrade(upgrade.id);
                          } else {
                            showNotification("Not enough gold!");
                          }
                        }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-bold">{upgrade.name}</h3>
                          <span className="bg-blue-950 px-2 py-1 rounded text-xs">
                            Level {currentUpgrade.level}
                          </span>
                        </div>
                        
                        <p className="text-sm text-blue-300 mb-3">{upgrade.description}</p>
                        
                        <div className="flex justify-between items-center mb-3">
                          <div className="text-sm">
                            Current bonus: 
                            <span className="font-medium text-green-400 ml-1">
                              {upgrade.stat === 'goldMultiplier' || upgrade.stat === 'experienceMultiplier'
                                ? `+${currentUpgrade.level * upgrade.baseEffect * 100}%`
                                : `+${currentUpgrade.level * upgrade.baseEffect}`}
                            </span>
                          </div>
                          
                          <div className="text-sm">
                            Next: 
                            <span className="font-medium text-yellow-400 ml-1">
                              {upgrade.stat === 'goldMultiplier' || upgrade.stat === 'experienceMultiplier'
                                ? `+${(currentUpgrade.level + 1) * upgrade.baseEffect * 100}%`
                                : `+${(currentUpgrade.level + 1) * upgrade.baseEffect}`}
                            </span>
                          </div>
                        </div>
                        
                        <div className={`flex items-center justify-center p-2 rounded ${
                          canAfford ? 'bg-green-800' : 'bg-red-900 bg-opacity-50'
                        }`}>
                          <Coins size={16} className="text-yellow-400 mr-2" />
                          <span>{currentUpgrade.cost} gold</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Inventory Tab */}
            {activeTab === 'inventory' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Inventory</h2>
                <p className="text-blue-300 mb-4">
                  Manage your hero's equipment and items.
                </p>
                
                <div className="bg-blue-900 bg-opacity-50 p-6 rounded-lg text-center">
                  <p className="text-lg mb-2">Inventory system coming soon!</p>
                  <p className="text-blue-300">
                    Future updates will include equipment, potions, and magical artifacts.
                  </p>
                </div>
              </div>
            )}
            
            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Settings</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg">
                    <h3 className="text-lg font-bold mb-3">Game Options</h3>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="mr-2" checked={true} onChange={() => {}} />
                          <span>Enable sound effects</span>
                        </label>
                      </div>
                      
                      <div>
                        <label className="flex items-center cursor-pointer">
                          <input type="checkbox" className="mr-2" checked={true} onChange={() => {}} />
                          <span>Auto-save game</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg">
                    <h3 className="text-lg font-bold mb-3">Game Data</h3>
                    
                    <div className="space-y-3">
                      <button 
                        onClick={saveGame}
                        className="w-full bg-green-700 hover:bg-green-600 py-2 rounded flex items-center justify-center"
                      >
                        <Save size={16} className="mr-2" /> Save Game
                      </button>
                      
                      <button 
                        onClick={() => {
                          if (confirm("Are you sure you want to reset your game? All progress will be lost!")) {
                            localStorage.removeItem('fantasyIdleGame');
                            window.location.reload();
                          }
                        }}
                        className="w-full bg-red-700 hover:bg-red-600 py-2 rounded flex items-center justify-center"
                      >
                        Reset Game
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 bg-blue-900 bg-opacity-50 p-4 rounded-lg">
                  <h3 className="text-lg font-bold mb-3">About</h3>
                  <p className="text-sm text-blue-300">
                    Fantasy Idle v1.0.0 - A fantasy-themed idle game where you can embark on adventures, 
                    upgrade your hero, and collect treasures in a magical world.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
